import { PatientModel, SurveyState } from '@context/UserContext/typing'
import { Roles } from '@survey/context/RoleProvider'
import { PATIENTS, PROVIDERS } from '../../route.const'
import {
  getFirstSurveyState,
  isAnySurveyCompleted,
  SURVEY_STATES,
} from '@util/surveyState'
import { useLocation, useNavigate } from 'react-router-dom'
import useUser from '@context/UserContext/useUser'
import { useAuth as providerUseAuth } from '@provider/context/AuthContext'
import { useAuth as patientUseAuth } from '@patient/context/AuthContext'
import { useEffect, useMemo, useState } from 'react'
import { ProviderModel } from '@provider/typing'
import { PureFunction } from '@typing/core'
import localStore from '@service/local-store'

interface IHeaderDetails {
  user?: {
    displayName: string
    degrees: string
    avatar: string
  }
  role: Roles
  login: boolean
  topMenu: HeaderOptionProps[]
  bottomMenu: HeaderOptionProps[]
  ipAddress: string
  surveyState: string
}

type ShowOn = (e: {
  pathName: string
  isProviderLoggedIn: boolean
  isPatientLoggedIn: boolean
  surveyState: SurveyState
}) => boolean

export interface HeaderOptionProps {
  label: string
  type: 'link' | 'button' | 'text'
  location: 'top' | 'bottom'
  onClick?: PureFunction
  path?: string
  hide?: boolean
  showOn: ShowOn
  subMenu?: Omit<HeaderOptionProps, 'showOn' | 'subMenu'>[]
}

const displayNavigation: (role: Omit<Roles, 'general'>) => ShowOn =
  (role) =>
  ({ pathName, isProviderLoggedIn, isPatientLoggedIn, surveyState }) => {
    // Allow navigation for guest users on paywall page (they completed survey)
    if (pathName.includes('/accesswall')) {
      const localSurveyState = localStore.get('surveyState')
      return role === PATIENTS && localSurveyState === 'Completed'
    }

    if (surveyState !== SURVEY_STATES.COMPLETED) return false
    return role === PROVIDERS
      ? isProviderLoggedIn
      : isPatientLoggedIn &&
          (pathName.endsWith('review') || !pathName.includes('survey'))
  }

const topMenu: HeaderOptionProps[] = [
  {
    label: 'About us',
    type: 'link',
    location: 'top',
    path: `/about-us`,
    showOn: ({ isProviderLoggedIn, isPatientLoggedIn }) => {
      return !(isProviderLoggedIn || isPatientLoggedIn)
    },
  },
  {
    label: 'FAQ',
    type: 'link',
    location: 'top',
    path: `/faq`,
    showOn: ({ isProviderLoggedIn, isPatientLoggedIn }) => {
      return !(isProviderLoggedIn || isPatientLoggedIn)
    },
  },
  {
    label: 'Contact us',
    type: 'link',
    location: 'top',
    path: `/contact-us`,
    showOn: ({ isProviderLoggedIn, isPatientLoggedIn }) => {
      return !(isProviderLoggedIn || isPatientLoggedIn)
    },
  },
]

const bottomMenu: HeaderOptionProps[] = [
  {
    label: 'Dashboard',
    type: 'link',
    location: 'bottom',
    path: `/${PROVIDERS}`,
    showOn: displayNavigation(PROVIDERS),
  },
  {
    label: 'My profile',
    type: 'link',
    location: 'bottom',
    path: `/${PROVIDERS}/profile`,
    showOn: displayNavigation(PROVIDERS),
  },
  {
    label: 'Manage responses',
    type: 'link',
    location: 'bottom',
    path: `/${PROVIDERS}/survey/review`,
    showOn: displayNavigation(PROVIDERS),
  },
  {
    label: 'My matches',
    hide: true,
    type: 'link',
    location: 'bottom',
    path: `/${PROVIDERS}/my-matches`,
    showOn: displayNavigation(PROVIDERS),
  },
  {
    label: 'My matches',
    type: 'link',
    location: 'bottom',
    path: `/${PATIENTS}/search-result`,
    showOn: displayNavigation(PATIENTS),
  },
  {
    label: 'My profile',
    type: 'link',
    hide: true,
    location: 'bottom',
    path: `/${PATIENTS}/profile`,
    showOn: displayNavigation(PATIENTS),
  },
  {
    label: 'My surveys',
    type: 'link',
    location: 'bottom',
    path: `/${PATIENTS}/survey/review`,
    showOn: displayNavigation(PATIENTS),
  },
  /*{
    label: 'My activity',
    type: 'link',
    location: 'bottom',
    path: `/${PATIENTS}/activity`,
    showOn: displayNavigation(PATIENTS),
  },*/
]

const useHeaderDetails = (): IHeaderDetails => {
  const [clientIPAddress, setClientIPAddress] = useState<string>('')
  const navigate = useNavigate()

  const location = useLocation()
  const { user, role } = useUser()

  const { isLoggedIn: isProviderLoggedIn } = providerUseAuth()
  const { isLoggedIn: isPatientLoggedIn } = patientUseAuth()

  const anyLogin = isProviderLoggedIn || isPatientLoggedIn

  const effectiveSurveyState = useMemo<SurveyState>(() => {
    if (!user) return SURVEY_STATES.NOT_STARTED

    if (role === PROVIDERS) {
      const providerDetails = user as ProviderModel
      return isAnySurveyCompleted(providerDetails.surveyState)
        ? SURVEY_STATES.COMPLETED
        : getFirstSurveyState(providerDetails.surveyState)
    }

    if (role === PATIENTS) {
      const patientDetails = user as PatientModel
      return isAnySurveyCompleted(patientDetails.surveyState)
        ? SURVEY_STATES.COMPLETED
        : getFirstSurveyState(patientDetails.surveyState)
    }

    return SURVEY_STATES.NOT_STARTED
  }, [role, user])

  useEffect(() => {
    const ipAddress = localStore.get('ipAddress')
    // Skip redirect for campaign landing pages and admin pages - they don't require ipAddress to be set
    const isCampaignPage = location.pathname.startsWith('/lp/')
    const isAdminPage = location.pathname.startsWith('/admin')

    if (ipAddress === null || ipAddress === '') {
      if (!isCampaignPage && !isAdminPage) {
        navigate('/')
      }
    } else {
      setClientIPAddress(ipAddress)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  const activeBottom = bottomMenu
    .filter(
      (header) =>
        !header.hide &&
        header.showOn({
          pathName: location.pathname,
          isProviderLoggedIn,
          isPatientLoggedIn,
          surveyState: effectiveSurveyState,
        })
    )
    .map((header) => {
      if (header.path?.includes(`/${PATIENTS}/survey/review`)) {
        const count = Object.values(
          (user as PatientModel).surveyState ?? {}
        ).filter((s) => s === SURVEY_STATES.COMPLETED).length
        return { ...header, label: count > 1 ? 'My surveys' : 'My survey' }
      }
      return header
    })
  const activeTop = topMenu.filter(
    (header) =>
      !header.hide &&
      header.showOn({
        pathName: location.pathname,
        isProviderLoggedIn,
        isPatientLoggedIn,
        surveyState: effectiveSurveyState,
      })
  )

  const userDetails = useMemo(
    () => {
      if (user && anyLogin) {
        if (role === PROVIDERS) {
          const providerDetails = { ...user } as ProviderModel
          const suffixPart = providerDetails.suffix
            ? ` ${providerDetails.suffix}`
            : ''

          return {
            displayName:
              providerDetails.firstName !== undefined
                ? `${providerDetails.firstName}  ${providerDetails.lastName}${suffixPart}`
                : '',
            degrees:
              providerDetails.degrees !== undefined
                ? providerDetails.degrees.toString()
                : '',
            avatar:
              providerDetails.images !== undefined &&
              providerDetails.images[0]?.primary
                ? providerDetails.images[0]?.thumbnailPath !== undefined
                  ? providerDetails.images[0]?.thumbnailPath
                  : providerDetails.images[0]?.path
                : '',
          }
        } else if (role === PATIENTS) {
          const patientDetails = { ...user } as PatientModel
          return {
            displayName:
              patientDetails.firstName !== undefined
                ? `${patientDetails.firstName}  ${
                    patientDetails?.lastName || ''
                  }`
                : '',
            degrees: '',
            avatar: '',
          }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [role, user]
  )

  return {
    login: anyLogin,
    user: userDetails,
    role: role,
    topMenu: activeTop,
    bottomMenu: activeBottom,
    ipAddress: clientIPAddress,
    surveyState: effectiveSurveyState,
  }
}

export default useHeaderDetails
